import { Icosahedron } from '@/components/ui/icons';
import { NestedTreeRows, type NestedTreeRow } from './NestedTreeRows';
import { useManualCadOptional } from '@/contexts/manual-cad-context';
import type { ManualStoredFeature } from '@/services/manualCad';

// A short human label for a feature row, e.g. "Pad (10mm)" / "Sketch (rect)".
function featureLabel(f: ManualStoredFeature): string {
  switch (f.type) {
    case 'Sketch': {
      const prof = (f as { profile?: { kind?: string } }).profile;
      return `Sketch (${prof?.kind ?? '…'})`;
    }
    case 'Pad':
      return `Pad (${(f as { length?: number }).length ?? '?'}mm)`;
    case 'Pocket':
      return `Pocket (${(f as { depth?: number }).depth ?? '?'}mm)`;
    case 'Fillet':
      return `Fillet (r${(f as { radius?: number }).radius ?? '?'})`;
    case 'Chamfer':
      return `Chamfer (${(f as { distance?: number }).distance ?? '?'})`;
    default:
      return f.type;
  }
}

/**
 * Sidebar content for Manual (FreeCAD-style) CAD mode. Renders the live feature
 * list of the active session as a Body → features tree (shared NestedTreeRows),
 * the Tip highlighted, plus busy / error state. Clicking a feature deletes it
 * (v1; a per-feature property editor is a later phase). See
 * docs/freecad-manual-cad-integration-plan.md §4.5.
 */
export function ManualCadContent() {
  const mc = useManualCadOptional();
  const features = mc?.features ?? [];

  if (features.length === 0) {
    return (
      <div className="py-1.5">
        <div className="flex items-center gap-2 py-1.5 pl-8 text-xs font-medium text-telekinesis-neutral-50">
          <Icosahedron className="h-3.5 w-3.5 shrink-0 text-telekinesis-neutral-400" />
          <span className="min-w-0 flex-1 truncate">Body</span>
        </div>
        <p className="px-8 py-3 text-xs leading-relaxed text-telekinesis-text-secondary">
          No features yet. Pick Sketch, Pad, Pocket, Fillet, Chamfer or Boolean
          from the toolbar to start building.
        </p>
      </div>
    );
  }

  const rows: NestedTreeRow[] = features.map((f) => ({
    key: f.id,
    label: featureLabel(f) + (f.id === mc?.tipId ? '  ● Tip' : ''),
    active: f.id === mc?.tipId,
    onClick: () => mc?.deleteFeature(f.id),
  }));

  return (
    <div>
      <NestedTreeRows
        parentLabel="Body"
        parentIcon={
          <Icosahedron className="h-3.5 w-3.5 shrink-0 text-telekinesis-neutral-400" />
        }
        rows={rows}
      />
      {mc?.busy && (
        <p className="px-8 py-2 text-xs text-telekinesis-text-secondary">
          Recomputing…
        </p>
      )}
      {mc?.error && (
        <p className="px-8 py-2 text-xs text-red-400">{mc.error}</p>
      )}
      <p className="px-8 pb-2 pt-1 text-[10px] leading-relaxed text-telekinesis-text-secondary">
        Click a feature to delete it. Parameter editing coming soon.
      </p>
    </div>
  );
}
